import type React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import { Button, H1, Icon, Link, P } from "@/base/components/Global";
import { Col, Row } from "@/base/components/Layout";
import { useUser } from "@/base/components/UserProvider";
import { navigateTo } from "@/base/utils/uriHelper";
import { APOLLO_CLUB_CARD_SMALL, LOGIN_ICON } from "@/constants/blobIcons";
import config from "@/constants/config";
import { NavigationPath } from "@/constants/navigation";
import { useAppSelector } from "@/store/hooks";
import { selectCurrentLanguage } from "@/store/language/selectors";
import { selectCurrentRegionPrefix } from "@/store/region/selectors";
import { getTheme } from "@/theme";

const SignInBlock: React.FC = () => {
  const { t } = useTranslation();
  const [{ logIn }] = useUser();
  const { isAuthenticated } = useAuth();
  const { color } = getTheme();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const currentRegionPrefix = useAppSelector(selectCurrentRegionPrefix);

  if (isAuthenticated) {
    // Authenticated User should not have any visible block
    return null;
  }

  const navigateToSignUp = () =>
    navigateTo(
      config.routerConfig.loyaltyProfileUrl,
      NavigationPath.SignUpStart,
      currentLanguage,
      currentRegionPrefix,
    );

  return (
    <>
      <Row aboveMd>
        <Col col={6} directionColumn noMarginLeft>
          <Row noMarginLeft>
            <Col col={12} noMarginBottom noMarginLeft>
              <H1 noMargin>{t("welcome_title")}</H1>
            </Col>
          </Row>
          <Row noMarginLeft>
            <Col col={12} noMarginLeft>
              <P black noMargin noMarginLeft>
                {t("sign_in_or_register_message")}
              </P>
            </Col>
          </Row>
          <Row noMarginLeft>
            <Col col={12} noMarginLeft>
              <Link brandColor action={navigateToSignUp}>
                {t("register_user")}
              </Link>
            </Col>
          </Row>
        </Col>
        <Col col={6} directionColumn noHorizontalMargin>
          <Row>
            <Col col={8}>
              <Link flex action={logIn}>
                <Icon icon={APOLLO_CLUB_CARD_SMALL} width={72} height={47} />
              </Link>
              <Row noMargin style={{ alignSelf: "flex-end" }}>
                <Col noVerticalMargin directionColumn>
                  <Link bold action={logIn}>
                    {t("sign_in_button")}
                  </Link>
                </Col>
              </Row>
            </Col>
            <Col
              alignEnd
              directionColumn
              justifyCenter
              style={{ alignSelf: "flex-end" }}
              noHorizontalMargin
            >
              <P xSmall noMargin>
                {t("new_user")}
              </P>
              <Link underline action={navigateToSignUp} xSmall>
                {t("create_account")}
              </Link>
            </Col>
          </Row>
          <Row>
            <Col col={12} noMarginRight>
              <Button
                data-testid="sign-in-button"
                prefixIcon={<Icon icon={LOGIN_ICON} stroke={color.white} />}
                action={logIn}
              >
                {t("sign_in_button")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row belowSm>
        <Col col={8}>
          <Link flex action={logIn}>
            <Icon icon={APOLLO_CLUB_CARD_SMALL} width={72} height={47} />
          </Link>
          <Row>
            <Col noMargin directionColumn>
              <Link bold action={logIn}>
                {t("sign_in_button")}
              </Link>
              <P xSmall noMargin>
                {t("sign_in_or_register_message")}
              </P>
            </Col>
          </Row>
        </Col>
        <Col alignEnd directionColumn justifyCenter>
          <P xSmall noMargin>
            {t("new_user")}
          </P>
          <Link underline action={navigateToSignUp} xSmall>
            {t("create_account")}
          </Link>
        </Col>
      </Row>
    </>
  );
};

export default SignInBlock;
